import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  getQboAccounts,
  getQboCustomers,
  getQboItems,
  linkCustomerToQbo,
  linkCostCodeToQbo,
  unlinkCustomerFromQbo,
  pushCustomerToQbo,
} from './quickbooksApi'
import {
  LinkCostCodePayload,
  LinkCustomerPayload,
  PushCustomerPayload,
  QboLinkResult,
} from './quickbooksModel'
import { useCostCodesInvalidator } from '../costCodes/costCodeQueries'

const QboQueryKeys = {
  QBO_CUSTOMERS: 'qboCustomers',
  QBO_ITEMS: 'qboItems',
  QBO_ACCOUNTS: 'qboAccounts',
} as const

export const useQboCustomersQuery = () => {
  return useQuery([QboQueryKeys.QBO_CUSTOMERS], () => getQboCustomers(), {
    refetchOnWindowFocus: false,
  })
}

// Customers not yet linked to SynkedUP, read fresh from QuickBooks, for the import picker.
// Shares the QBO_CUSTOMERS prefix so useQboCustomersInvalidator refreshes both lists.
export const useUnlinkedQboCustomersQuery = (enabled: boolean) => {
  return useQuery(
    [QboQueryKeys.QBO_CUSTOMERS, 'unlinked'],
    () => getQboCustomers({ filterAssigned: true, disableCache: true }),
    { enabled, retry: false, refetchOnWindowFocus: false }
  )
}

export const useQboCustomersInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidateQboCustomers: () => queryClient.invalidateQueries([QboQueryKeys.QBO_CUSTOMERS]),
  }
}

export const useQboItemsQuery = () => {
  return useQuery([QboQueryKeys.QBO_ITEMS], getQboItems, {
    refetchOnWindowFocus: false,
  })
}

export const useQboAccountsQuery = () => {
  return useQuery([QboQueryKeys.QBO_ACCOUNTS], getQboAccounts, {
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'There was a problem loading QuickBooks accounts.' })
    },
  })
}

export const useLinkCustomerToQboMutation = (onSuccess?: (result: QboLinkResult) => void) => {
  return useMutation({
    mutationFn: (payload: LinkCustomerPayload) => linkCustomerToQbo(payload),
    onSuccess: (_data, variables) => {
      onSuccess?.({ qboId: variables.qboId, qboDisplayName: variables.qboDisplayName })
    },
    onError: () => {
      notification.error({ message: 'Failed to link customer to QuickBooks' })
    },
  })
}

export const useUnlinkCustomerFromQboMutation = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: (customerId: number) => unlinkCustomerFromQbo(customerId),
    onSuccess: () => {
      onSuccess?.()
    },
    onError: () => {
      notification.error({ message: 'Failed to unlink customer from QuickBooks' })
    },
  })
}

export const usePushCustomerToQboMutation = (onSuccess?: (result: QboLinkResult) => void) => {
  return useMutation({
    mutationFn: (payload: PushCustomerPayload) => pushCustomerToQbo(payload),
    onSuccess: (data) => {
      notification.success({ message: 'Customer successfully created in QuickBooks' })
      onSuccess?.({ qboId: data.entity.id, qboDisplayName: data.entity.displayName })
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Failed to push customer to QuickBooks'
      notification.error({ message: msg })
    },
  })
}

export const useLinkCostCodeToQboMutation = (onSuccess?: (result: QboLinkResult) => void) => {
  const costCodesInvalidator = useCostCodesInvalidator()
  return useMutation({
    mutationFn: (payload: LinkCostCodePayload) => linkCostCodeToQbo(payload),
    onSuccess: (_data, variables) => {
      onSuccess?.({
        qboId: variables.qboId,
        qboDisplayName: variables.qboDisplayName ?? '',
      })
      costCodesInvalidator.invalidate()
    },
    onError: () => {
      notification.error({ message: 'Failed to link cost code to QuickBooks' })
    },
  })
}
